import { Typography } from "@mui/material";
import HtmlWithLatex from "../pages/QuestionBank/components/HtmlWithLatex";

// Strip ALL leading "N. " or "N) " — fixes duplicate numbering from AI/import (e.g. "2. 1. You have...").
const stripAllLeadingNumbers = (s) => {
  let result = s.trim();
  let prev = "";
  while (prev !== result) {
    prev = result;
    result = result.replace(/^\d+[.)]\s+/, "").trim();
  }
  return result;
};

/**
 * Renders exam/section instructions with proper numbering (not paragraph-style).
 * Handles: HTML lists, plain text with newlines, single-line numbered text, bullet markers.
 */
const FormatInstructions = ({ text, typographySx = {} }) => {
  if (!text) return null;

  const hasHtmlTags = /<[^>]+>/.test(text);

  if (hasHtmlTags) {
    const hasNumberedList = /<ol/i.test(text) || /<li/i.test(text);
    const hasBulletList = /<ul/i.test(text);

    if (hasNumberedList || hasBulletList) {
      return <HtmlWithLatex html={text} />;
    }

    const cleaned = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    const sentences = cleaned.split(/(?<=[.!?])\s+/).filter((s) => s.trim());

    if (sentences.length <= 1) {
      return <HtmlWithLatex html={text} />;
    }

    const displaySentences = sentences.map((s) => stripAllLeadingNumbers(s));
    const listItems = displaySentences.map((sentence, index) => (
      <li key={index} style={{ marginBottom: "8px", lineHeight: 1.6 }}>
        {sentence.trim()}
      </li>
    ));

    return (
      <ol style={{ margin: 0, paddingLeft: "20px" }}>
        {listItems}
      </ol>
    );
  }

  const lines = text.split("\n").filter((line) => line.trim());

  if (lines.length <= 1) {
    const numberedPattern = /(?=\d+[.)]\s)/;
    const parts = text.trim().split(numberedPattern).filter((p) => p.trim());
    const hasMultipleNumbered =
      parts.length > 1 && parts.some((p) => /^\d+[.)]\s/.test(p.trim()));
    if (hasMultipleNumbered) {
      const displayParts = parts.map((p) => stripAllLeadingNumbers(p.trim()));
      const listItems = displayParts.map((part, index) => (
        <li key={index} style={{ marginBottom: "8px", lineHeight: 1.6 }}>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.875rem", lineHeight: 1.6, ...typographySx }}
          >
            {part}
          </Typography>
        </li>
      ));
      return (
        <ol style={{ margin: 0, paddingLeft: "20px", listStyleType: "decimal" }}>
          {listItems}
        </ol>
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{ fontSize: "0.875rem", lineHeight: 1.6, whiteSpace: "pre-line", ...typographySx }}
      >
        {text}
      </Typography>
    );
  }

  const hasBulletMarkers = lines.every((line) =>
    /^[\u2713\u2714\u2022\u2023\-–*>]\s/.test(line.trim())
  );
  if (hasBulletMarkers) {
    return (
      <ul style={{ margin: 0, paddingLeft: "4px", listStyle: "none" }}>
        {lines.map((line, index) => (
          <li key={index} style={{ marginBottom: "8px", lineHeight: 1.6 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.875rem", lineHeight: 1.6, ...typographySx }}
            >
              {line.trim()}
            </Typography>
          </li>
        ))}
      </ul>
    );
  }

  const displayLines = lines.map((line) => stripAllLeadingNumbers(line));
  const listItems = displayLines.map((line, index) => (
    <li key={index} style={{ marginBottom: "8px", lineHeight: 1.6 }}>
      <Typography
        variant="body2"
        sx={{ fontSize: "0.875rem", lineHeight: 1.6, ...typographySx }}
      >
        {line.trim()}
      </Typography>
    </li>
  ));

  return (
    <ol style={{ margin: 0, paddingLeft: "20px" }}>
      {listItems}
    </ol>
  );
};

export default FormatInstructions;
